import React, { useEffect, useState, useMemo } from 'react';

import CreateButton from '../../../elements/CreateButton';
import DeleteButton from '../../../elements/DeleteButton';
import EditButton from '../../../elements/EditButton';
import Loader from '../../../elements/Loader';
import Table from '../../../elements/Table';
import ViewButton from '../../../elements/ViewButton';
import QuestionMark from '../../../elements/common/QuestionMark';
import Paginator from '../../../elements/paginator';
import { PermissionType } from '../../../helpers/Permissions';
import axios from '../../../services/MyAxios';
import { replaceUrlParams } from '../Helpers/UrlHelper';

interface BonusPage {
    id: number;
    title: string;
    url: string;
    language: {
        code: string;
    };
    // eslint-disable-next-line camelcase
    image_path: string;
}

interface DetailsAccessor {
    id: number;
    canDelete: boolean;
}

interface Props {
    showUrl: string;
    createUrl: string;
    editUrl: string;
    deleteUrl: string;
    apiUrl: string;
    showPermission: PermissionType;
    editPermission: PermissionType;
    deletePermission: PermissionType;
    createPermission: PermissionType;
    bonusIdColumn: string;
    baseFrontLink: string;
}

function BonusPagesList({
    showUrl,
    createUrl,
    editUrl,
    deleteUrl,
    apiUrl,
    showPermission,
    editPermission,
    deletePermission,
    createPermission,
    bonusIdColumn,
    baseFrontLink,
}: Props) {
    const [bonusPages, setBonusPages] = useState<BonusPage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    const getBonusPages = (page = 1) => {
        let apiUrlWithPagination = apiUrl;

        if (page !== 1) {
            apiUrlWithPagination += `?page=${page}`;
        }

        axios.get(apiUrlWithPagination).then((response) => {
            const updatedData = response.data.data.map((element: BonusPage) => {
                const detailsAccessor: DetailsAccessor = { id: element.id, canDelete: element[bonusIdColumn] === null };
                return { ...element, details_accessor: detailsAccessor };
            });

            setBonusPages(updatedData);
            setCurrentPage(response.data.current_page);
            setLastPage(response.data.last_page);
        });
    };

    useEffect(() => {
        getBonusPages();
        setIsLoading(false);
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'Title',
                accessor: 'title',
                Cell: ({ value }: { value: string }) => <div>{value}</div>,
            },
            {
                Header: 'Slug',
                accessor: 'slug',
                Cell: ({ value }: { value: string }) => (
                    <div>
                        <a href={`${baseFrontLink}/${value}`} target="_blank" rel="noreferrer">
                            {value}
                        </a>
                        <QuestionMark definition="The page has to be connected to bonus in order to display it on front" />
                    </div>
                ),
            },
            {
                Header: 'Language',
                accessor: 'language.name',
            },
            {
                Header: 'Image',
                accessor: 'image_path',
                Cell: ({ value }: { value: string }) => <img style={{ maxWidth: '25%' }} src={`${value}`} alt="" />,
            },
            {
                Header: 'Actions',
                accessor: 'details_accessor',
                Cell: ({ value }: { value: DetailsAccessor }) => (
                    <div>
                        <ViewButton
                            url={replaceUrlParams(showUrl, { id: value.id })}
                            permissions={[showPermission]}
                            key="view"
                        />
                        <EditButton
                            url={replaceUrlParams(editUrl, { id: value.id })}
                            permissions={[editPermission]}
                            key="edit"
                        />
                        {value.canDelete && (
                            <DeleteButton
                                url={replaceUrlParams(deleteUrl, { id: value.id })}
                                action={getBonusPages}
                                permissions={[deletePermission]}
                                key="delete"
                            />
                        )}
                    </div>
                ),
            },
        ],
        [showUrl, createUrl, editUrl, deleteUrl, showPermission, editPermission, deletePermission]
    );

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between">
                <span />
                <CreateButton
                    url={createUrl}
                    permissions={[createPermission]}
                    key="create"
                    buttonText="Create a new bonus page"
                />
            </div>
            <div className="card-body">
                <Loader loading={isLoading} content={() => <Table columns={columns} data={bonusPages} />} />
                <Paginator currentPage={currentPage} lastPage={lastPage} onPageChange={getBonusPages} />
            </div>
        </div>
    );
}

export default BonusPagesList;
